import { useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import type { SaleFunnelType } from '@customTypes/salesFunnels';
import { datadogRum } from '@datadog/browser-rum';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
  saleFunnel?: SaleFunnelType,
};

const UpsertDialog = ({ handleClose, afterSubmit, saleFunnel }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);

  const isEditing = !!saleFunnel;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    const name = capitalizeFirstLetter(data.name as string);
    
    try {
      let response;

      if (isEditing) {
        response = await api.put(`/sales_funnel/${saleFunnel!.id}`, {
          name,
        });
      } else {
        response = await api.post('/sales_funnel', {
          name,
        });
      }

      if (response?.status === 200) {
        toast.success(`Funil de venda ${isEditing ? 'editado' : 'criado'} com sucesso!`);
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      toast.error(`Erro ao ${isEditing ? 'editar' : 'criar'} funil de venda. Verifique os dados e tente novamente.`);
      datadogRum.addError(new Error(`Fail to ${isEditing ? 'edit' : 'create'} sales funnel`), {
        error,
        saleFunnelId: saleFunnel?.id,
        name,
      });

    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>
                {isEditing ? 'Editar funil de venda' : 'Novo funil de venda'}
              </Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.name}>
                  <Field.Label>Nome</Field.Label>
                  <InputGroup width="full" gap={0}>
                    <Input
                      name="name"
                      placeholder="Digite aqui..."
                      minLength={3}
                      variant="subtle"
                      required
                      defaultValue={saleFunnel?.name || ''}
                    />
                  </InputGroup>
                  {errors?.name?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UpsertDialog;
